import type { RankingParticipantSummary } from '@/queries/cpmRanking';
import type { RankingPlatform } from './rankingCompetition';

export const PARTICIPANT_SUMMARY_PLATFORMS = ['tiktok', 'instagram', 'youtube', 'facebook'] as const satisfies readonly RankingPlatform[];
export type RankingParticipantPlatformPoint = { platform: RankingPlatform; exactViews: string; countedPostCount: number; views: number };
export type RankingParticipantDayPoint = { date: string; exactViews: string; countedPostCount: number; views: number };
type SummaryBreakdowns = Pick<RankingParticipantSummary, 'by_platform' | 'by_publication_day'>;
type ParticipantChartData = { platforms: RankingParticipantPlatformPoint[] | null; timeline: RankingParticipantDayPoint[] | null };

const MAX_CALENDAR_DAYS = 3_660;
const DAY_MS = 86_400_000;
const validViews = (value: string) => typeof value === 'string' && /^\d+$/.test(value);
const validPosts = (value: number) => Number.isSafeInteger(value) && value >= 0;
const validDate = (date: string) => /^\d{4}-\d{2}-\d{2}$/.test(date)
  && Number.isFinite(Date.parse(`${date}T00:00:00Z`))
  && new Date(`${date}T00:00:00Z`).toISOString().slice(0, 10) === date;

function plotValues<T extends { exactViews: string }>(points: T[]): (T & { views: number })[] {
  const maximum = points.reduce((max, point) => {
    const views = BigInt(point.exactViews);
    return views > max ? views : max;
  }, 0n);
  // Axes show categories only. Scale very large series together to preserve
  // proportions without Infinity; every label and tooltip uses exactViews.
  return points.map(point => ({
    ...point,
    views: maximum > BigInt(Number.MAX_SAFE_INTEGER)
      ? Number(BigInt(point.exactViews) * 1_000_000_000n / maximum) / 1_000
      : Number(point.exactViews),
  }));
}

function competitionCalendar(timezone: string, startDate?: string | null, endDate?: string | null): string[] {
  const start = Date.parse(startDate || '');
  const end = Date.parse(endDate || '');
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return [];
  try {
    const formatter = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' });
    const localDate = (instant: number) => {
      const parts = formatter.formatToParts(new Date(instant));
      return ['year', 'month', 'day'].map(part => parts.find(item => item.type === part)?.value).join('-');
    };
    const first = localDate(start);
    const last = localDate(end - 1);
    if (!validDate(first) || !validDate(last)) return [];
    const firstDay = Date.parse(`${first}T00:00:00Z`);
    const lastDay = Date.parse(`${last}T00:00:00Z`);
    const days = (lastDay - firstDay) / DAY_MS + 1;
    if (days < 1 || days > MAX_CALENDAR_DAYS) return [];
    // Step over calendar labels in UTC so DST cannot skip or repeat a date.
    return Array.from({ length: days }, (_, index) => new Date(firstDay + index * DAY_MS).toISOString().slice(0, 10));
  } catch {
    return [];
  }
}

function platformPoints(rows: SummaryBreakdowns['by_platform']): RankingParticipantPlatformPoint[] | null {
  if (!Array.isArray(rows) || rows.length !== PARTICIPANT_SUMMARY_PLATFORMS.length) return null;
  const points: Omit<RankingParticipantPlatformPoint, 'views'>[] = [];
  for (const platform of PARTICIPANT_SUMMARY_PLATFORMS) {
    const matches = rows.filter(row => row.platform === platform);
    if (matches.length !== 1 || !validViews(matches[0].counted_views) || !validPosts(matches[0].counted_post_count)) return null;
    points.push({ platform, exactViews: BigInt(matches[0].counted_views).toString(), countedPostCount: matches[0].counted_post_count });
  }
  return plotValues(points);
}

function publicationPoints(rows: SummaryBreakdowns['by_publication_day'], timezone: string, startDate?: string | null, endDate?: string | null): RankingParticipantDayPoint[] | null {
  if (!Array.isArray(rows)) return null;
  const dates = new Map<string, { views: bigint; posts: number }>();
  for (const row of rows) {
    if (!validDate(row.date) || !validViews(row.counted_views) || !validPosts(row.counted_post_count)) return null;
    const previous = dates.get(row.date);
    const posts = (previous?.posts ?? 0) + row.counted_post_count;
    if (!validPosts(posts)) return null;
    dates.set(row.date, { views: (previous?.views ?? 0n) + BigInt(row.counted_views), posts });
  }
  for (const date of competitionCalendar(timezone, startDate, endDate)) {
    if (!dates.has(date)) dates.set(date, { views: 0n, posts: 0 });
  }
  return plotValues([...dates].sort(([a], [b]) => a.localeCompare(b)).map(([date, totals]) => ({ date, exactViews: totals.views.toString(), countedPostCount: totals.posts })));
}

/** Whole-campaign aggregates only; never accepts standings or private reports. */
export function buildRankingParticipantCharts(summary: SummaryBreakdowns | null | undefined, timezone: string, startDate?: string | null, endDate?: string | null): ParticipantChartData {
  return {
    platforms: platformPoints(summary?.by_platform),
    timeline: publicationPoints(summary?.by_publication_day, timezone, startDate, endDate),
  };
}
